import { Component, Element, h, Prop, getAssetPath, Listen, State, Fragment, EventEmitter } from '@stencil/core'
import { retailSearchResultsPage, coveoAnalyticsApiUrl, profileLinkApiUrl } from 'src/global/resources'
import { QuerySuggestDataGroup } from 'src/model/query-suggest.model'
import { ValuesDataGroup } from 'src/model/values.model'
import { postData, fetchData } from 'src/utils/http-requests.util'
import { CoveoAnalytics } from 'src/model/coveo-analytics.model'
import { searchResultClick } from 'src/model/search-result-clicked-object.model'

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions, @typescript-eslint/no-explicit-any
declare global {interface Window { dataLayer?: any[] }}

@Component({
  tag: 'search-dropdown',
  styleUrl: 'search-dropdown.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class SearchDropdown {
  @Prop() expanded: boolean
  @Prop() recentSearches: string[] = []
  @Prop() handleUpdateRecentSearches: (expression: string) => void
  @Prop() updateRecentSearches: (expression: string) => void
  @Prop() products: ValuesDataGroup[] = []
  @Prop() suggestion: QuerySuggestDataGroup[] = []
  @Prop() searchQuery = ''
  @Prop() handleViewAllResults: () => void
  @Prop() handleRemoveComponent: () => void
  @Prop() previousNavigationEvent: EventEmitter<string>
  @Prop() createSearchClickObject: (searchTermClicked: string, searchType: string) => void
  @Prop() searchResultClickedHandler: (searchResultClickObj: searchResultClick) => void
  @Prop() searchResultClicked: EventEmitter<searchResultClick>
  @Prop() initializeSearchResultClickObj: () => void
  @Prop() postCoveoAnalyticsBody: CoveoAnalytics
  @Prop() searchResultClickObj: searchResultClick
  @State() onMobile = false
  @State() visualViewPortHeight: number = window.visualViewport.height

  @Element() el: HTMLElement

  // Lifecycle Methods
  componentWillLoad() {
    this.handlerResize()
    window.visualViewport.addEventListener('resize', this.handleVisualViewPort)
    // Initialize adobe analytics object
    this.initializeSearchResultClickObj()
  }

  componentDidLoad() {
    this.toggleBodyStyle(window.innerWidth <= 990)
  }

  disconnectedCallback() {
    window.visualViewport.removeEventListener('resize', this.handleVisualViewPort)
    this.toggleBodyStyle(false)
  }

  // Event Listeners
  @Listen('resize', { target: 'window' })
  handlerResize() {
    this.toggleBodyStyle(window.innerWidth <= 990)
    this.onMobile = window.innerWidth <= 576 || window.innerHeight <= 448
    if (window.innerWidth > window.innerHeight && this.onMobile) {
      this.toggleBodyStyle(true)
    }
  }

  private async handleOnClickProduct(productSummary: string) {
    try {
      this.postCoveoAnalyticsBody = {
        user: 'sxp-web-component',
        eventType: 'ProductListHeader',
        eventValue: 'onClick',
        language: 'en',
        originLevel1: 'RetailSearchHub',
        customData: {
          selectedProduct: productSummary,
        },
      }
      // call analytics api here
      await postData<CoveoAnalytics>(coveoAnalyticsApiUrl, this.postCoveoAnalyticsBody)
      // fetching the profile link which redirect to investment products page
      const res = await fetchData<Record<string, string>>(profileLinkApiUrl, encodeURIComponent(productSummary))
      window.location.assign(res.profileLink)
    } catch (error) {
      console.error(error)
    }
    this.createSearchClickObject(productSummary, 'autosuggest click - product')
  }

  private handleOnClickSuggestion(expression: string) {
    try {
      const encodeURL = encodeURI(expression)
      this.handleUpdateRecentSearches(expression)
      this.toggleBodyStyle(false)
      this.previousNavigationEvent.emit('backArrow')
      window.location.assign(`${retailSearchResultsPage}${encodeURL}`)
      setTimeout(() => {
        console.log('Inside the handleOnClickSuggestions')
      }, 1000)
    } catch (error) {
      console.error(error)
    }
    this.createSearchClickObject(expression, 'autosuggest click')
  }

  private handleVisualViewPort = () => {
    this.visualViewPortHeight = window.visualViewport.height
  }

  private toggleBodyStyle(disable: boolean) {
    document.body.style.overflow = disable ? 'hidden' : ''
  }

  render() {
    if (this.suggestion.length >= 1 || this.products.length >= 1) {
      return (
        <div
          class="search-widget-dropdown c11n-dropdown__menu c11n-menu__menu c11n-elevation-800"
          role="menu"
          id="searchWidgetDropdown"
          style={{ maxHeight: `${this.visualViewPortHeight - 64}px` }}
        >
          {this.suggestion.length >= 1 && (
            <Fragment>
              <div class="search-widget-dropdown-list">
                <div class="search-widget-dropdown-listname search-widget-text"><b>Suggestions</b></div>
                <div role="group" aria-label="Suggestion List">
                  {this.suggestion.map((item: QuerySuggestDataGroup, index: number) => {
                    const listItem = (
                      <button
                        onClick={() => this.handleOnClickSuggestion(item.expression)}
                        role="menuitem"
                        class="search-widget-dropdown-list-item search-widget-text"
                        aria-label={item.expression}
                      >
                        <img src={getAssetPath('./assets/search-small.png')} width={14} height={14} alt="suggestion" />
                        <p>{item.expression}</p>
                      </button>
                    )

                    if (this.onMobile) {
                      if (index <= 2) {
                        return (listItem)
                      }
                    } else if (index <= 4) {
                      return (listItem)
                    }
                  })}
                </div>
              </div>
              <hr />
            </Fragment>
          )}
          {this.products.length >= 1 && (
            <Fragment>
              <div class="search-widget-dropdown-list search-widget-text">
                <div class="search-widget-dropdown-listname search-widget-text"><b>Products</b></div>
                <div role="group" aria-label="Product List" class="products-list">
                  {this.products.map((item: ValuesDataGroup, index: number) => {
                    const data = item.value.split(' - ')
                    const ticker = data[0]
                    const summary = data[1]
                    const reg = new RegExp(this.searchQuery, 'gi')
                    const final_str = summary.replace(reg, function (str) {
                      return '<b>' + str + '</b>'
                    })
                    const listItem = (
                      <button
                        role="menuitem"
                        class="search-widget-dropdown-list-item search-widget-text"
                        onClick={() => this.handleOnClickProduct(item.value)}
                        aria-label={ticker}
                      >
                        <b class="product-name">{ticker}</b>
                        <p innerHTML={final_str}></p>
                      </button>
                    )

                    if (this.onMobile) {
                      if (index <= 2) {
                        return (listItem)
                      }
                    } else if (index <= 4) {
                      return (listItem)
                    }
                  })}
                </div>
              </div>
              <hr />
            </Fragment>
          )}
          <div class="search-widget-dropdown-list search-widget-text">
            <button
              role="menuitem"
              class="search-widget-dropdown-list-item search-widget-text no-background py-0"
              onClick={() => {
                this.handleViewAllResults()
                this.previousNavigationEvent.emit('backArrow')
                this.createSearchClickObject('View All Results', 'autosuggest click - view all results')
              }}
            >
              <b>View all results</b>
            </button>
          </div>
        </div>
      )
    } else if ((this.searchQuery.length === 0 || this.searchQuery === '') && this.recentSearches.length > 0) {
      return (
        <div
          class="search-widget-dropdown c11n-dropdown__menu c11n-menu__menu c11n-elevation-800"
          role="menu"
          id="searchWidgetDropdown"
        >
          <div class="search-widget-dropdown-list">
            <div class="search-widget-dropdown-listname search-widget-text"><b>Recent searches</b></div>
            <div role="group" aria-label="Recent Searches List">
              {this.recentSearches.map((item: string) => {
                const listItem = (
                  <button
                    onClick={() => this.handleOnClickSuggestion(item)}
                    role="menuitem"
                    class="search-widget-dropdown-list-item search-widget-text"
                    aria-label={item}
                  >
                    <img src={getAssetPath('./assets/rewind.png')} width={14} height={14} alt="recent search" />
                    <p>{item}</p>
                  </button>
                )
                return listItem
              })}
            </div>
          </div>
          <hr />
          <div class="search-widget-dropdown-list search-widget-text hidden">
          </div>
        </div>
      )
    }
  }
}
